import { Routes } from '@angular/router';
import { AuthRedirectGuard } from './guards/auth-guard';
import { AuthResolver } from './services/auth-resolver';
import { SalesforceComponent } from './components/salesforce/salesforce.component';
import { BackgroundProcessGuard } from './guards/background-process-guard';

export const routes: Routes = [
    {
        path: 'oauth2/callback/authentication_token',
        component: SalesforceComponent,
    },
    {
        path: 'dashboard',
        loadComponent: () => import('./components/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        canActivate: [AuthRedirectGuard],
        resolve: { auth: AuthResolver },
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'general',
            },
            {
                path: 'general',
                loadComponent: () => import('./components/general/general.component').then((m) => m.GeneralComponent),
            },
            {
                path: 'voice-recordings',
                loadComponent: () =>
                    import('./components/voice-recordings/voice-recordings.component').then((m) => m.VoiceRecordingsComponent),
            },
        ],
    },
    {
        path: 'upload',
        loadComponent: () => import('./components/upload/upload.component').then((m) => m.UploadComponent),
        canActivate: [AuthRedirectGuard],
        canDeactivate: [BackgroundProcessGuard],
        resolve: { auth: AuthResolver },
    },
    {
        path: 'scanned',
        loadComponent: () => import('./components/scanned/scanned.component').then((m) => m.ScannedComponent),
        canActivate: [AuthRedirectGuard],
        canDeactivate: [BackgroundProcessGuard],
        resolve: { auth: AuthResolver },
    },
    {
        path: 'reviewed',
        loadComponent: () => import('./components/reviewed/reviewed.component').then((m) => m.ReviewedComponent),
        canActivate: [AuthRedirectGuard],
        canDeactivate: [BackgroundProcessGuard],
        resolve: { auth: AuthResolver },
    },
    {
        path: 'library',
        loadComponent: () => import('./components/library/library.component').then((m) => m.LibraryComponent),
        canActivate: [AuthRedirectGuard],
        resolve: { auth: AuthResolver },
    },
    {
        path: 'library/audits',
        loadComponent: () => import('./components/library-audits/library-audits.component').then((m) => m.LibraryAuditsComponent),
        canActivate: [AuthRedirectGuard],
        resolve: { auth: AuthResolver },
    },
    {
        // Moved out of Settings and promoted to its own sidebar item, per the redesign.
        path: 'activity',
        loadComponent: () =>
            import('./components/settings-activity-summary/settings-activity-summary.component').then(
                (m) => m.SettingsActivitySummaryComponent
            ),
        canActivate: [AuthRedirectGuard],
        resolve: { auth: AuthResolver },
    },
    {
        path: 'settings',
        loadComponent: () => import('./components/settings/settings.component').then((m) => m.SettingsComponent),
        canActivate: [AuthRedirectGuard],
        resolve: { auth: AuthResolver },
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'details',
            },
            {
                // Kept so old bookmarks/links to the previous location still work.
                path: 'activity-summary',
                redirectTo: '/activity',
            },
            {
                path: 'clients',
                loadComponent: () =>
                    import('./components/settings-clients/settings-clients.component').then((m) => m.SettingsClientsComponent),
            },
            {
                path: 'users',
                loadComponent: () => import('./components/settings-users/settings-users.component').then((m) => m.SettingsUsersComponent),
            },
            {
                path: 'details',
                loadComponent: () =>
                    import('./components/settings-details/settings-details.component').then((m) => m.SettingsDetailsComponent),
            },
            {
                path: 'payments',
                loadComponent: () =>
                    import('./components/settings-payments/settings-payments.component').then((m) => m.SettingsPaymentsComponent),
            },
            {
                path: 'workflow-preferences',
                loadComponent: () =>
                    import('./components/workflow-preferences/workflow-preferences.component').then((m) => m.WorkflowPreferencesComponent),
            },
            {
                path: 'outreach',
                loadComponent: () =>
                    import('./components/settings-outreach/settings-outreach.component').then((m) => m.SettingsOutreachComponent),
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'email',
                    },
                    {
                        path: 'email',
                        loadComponent: () =>
                            import('./components/settings-outreach-email/settings-outreach-email.component').then(
                                (m) => m.SettingsOutreachEmailComponent
                            ),
                    },
                    {
                        path: 'sms',
                        loadComponent: () =>
                            import('./components/settings-outreach-sms/settings-outreach-sms.component').then(
                                (m) => m.SettingsOutreachSmsComponent
                            ),
                    },
                ],
            },
            {
                path: 'admin',
                loadComponent: () => import('./components/settings-admin/settings-admin.component').then((m) => m.SettingsAdminComponent),

                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'reports',
                    },
                    {
                        path: 'payment',
                        loadComponent: () =>
                            import('./components/settings-payment-admin/settings-payment-admin.component').then(
                                (m) => m.SettingsPaymentAdminComponent
                            ),
                    },
                    {
                        path: 'reports',
                        loadComponent: () =>
                            import('./components/settings-reports-admin/settings-reports-admin.component').then(
                                (m) => m.SettingsReportsAdminComponent
                            ),
                    },
                ],
            },
        ],
    },
    {
        path: 'forbidden',
        loadComponent: () => import('./components/forbidden-page/forbidden-page.component').then((m) => m.ForbiddenPageComponent),
    },
    {
        path: 'salesforce',
        loadComponent: () => import('./components/salesforce/salesforce.component').then((m) => m.SalesforceComponent),
        canActivate: [AuthRedirectGuard],
        resolve: { auth: AuthResolver },
    },
    {
        path: 'login',
        loadComponent: () => import('./components/login/login.component').then((m) => m.LoginComponent),
    },
    {
        path: 'register',
        loadComponent: () => import('./components/register/register.component').then((m) => m.RegisterComponent),
    },
    {
        path: 'logout',
        loadComponent: () => import('./components/logout/logout.component').then((m) => m.LogoutComponent),
    },
    {
        path: 'unsubscribe/:id',
        loadComponent: () => import('./components/unsubscribe/unsubscribe.component').then((m) => m.UnsubscribeComponent),
    },
    { path: '**', redirectTo: 'login' },
];
